import React, { useEffect, useState } from "react";
import { Sparkles, Check, X, MessageSquare, Loader2, ClipboardCheck, Building2, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STATUS_STYLES = {
    pending_review: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    processing: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    ready: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    declined: "bg-destructive/10 text-destructive border-destructive/30",
    failed: "bg-destructive/10 text-destructive border-destructive/30",
};

export const BriefReviewsTab = () => {
    const [briefs, setBriefs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending_review");
    const [reviewing, setReviewing] = useState(null); // { brief, action }
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/admin/briefs${filter && filter !== "all" ? `?status=${filter}` : ""}`);
            setBriefs(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // `load` reads `filter` from closure; adding `load` to deps would cause a
    // re-fetch on every render since it isn't memoised. Depending on `filter`
    // is the intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filter]);

    const openReview = (brief, action) => {
        setReviewing({ brief, action });
        setNote("");
    };

    const submit = async () => {
        if (!reviewing) return;
        try {
            setSubmitting(true);
            await api.post(`/admin/briefs/${reviewing.brief.id}/review`, {
                action: reviewing.action,
                note,
            });
            toast.success(
                reviewing.action === "approve" ? "Brief approved. AI matching started." :
                    reviewing.action === "decline" ? "Brief declined. Employer notified." :
                        "Info requested. Employer notified."
            );
            setReviewing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const pendingCount = briefs.filter((b) => b.status === "pending_review").length;

    return (
        <div data-testid="brief-reviews-tab">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        { v: "pending_review", label: `Pending review${filter !== "pending_review" ? "" : ` (${pendingCount})`}` },
                        { v: "processing", label: "Processing" },
                        { v: "ready", label: "Approved & Ready" },
                        { v: "declined", label: "Declined" },
                        { v: "all", label: "All" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            data-testid={`brief-filter-${f.v}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.v ? "bg-brand-navy text-white border-brand-navy" : "border-border text-muted-foreground hover:text-foreground"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <button onClick={load} className="btn-outline text-xs" data-testid="brief-refresh">
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="py-12 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : briefs.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <ClipboardCheck size={22} className="text-emerald-500 mx-auto" />
                    <h3 className="mt-3 font-heading font-semibold text-brand-navy dark:text-slate-100">All caught up.</h3>
                    <p className="text-sm text-muted-foreground mt-1">No briefs in this bucket right now.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {briefs.map((brief) => (
                        <div key={brief.id} className="card-soft p-5" data-testid={`brief-review-${brief.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-semibold text-brand-navy dark:text-slate-100">
                                            {brief.job_profile?.title || brief.triage?.role_category || (brief.status === "pending_review" ? "New brief" : "Untitled brief")}
                                        </h3>
                                        <Badge className={`text-[10px] border ${STATUS_STYLES[brief.status] || STATUS_STYLES.pending_review}`}>
                                            {brief.status.replace("_", " ")}
                                        </Badge>
                                        {brief.source_type && (
                                            <Badge variant="outline" className="text-[10px] border-border">via {brief.source_type}</Badge>
                                        )}
                                        {brief.triage?.score !== undefined && (
                                            <Badge className={`text-[10px] border ${brief.triage.score >= 75 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : brief.triage.score >= 50 ? "border-amber-500/40 bg-amber-500/10 text-amber-600" : "border-destructive/40 bg-destructive/10 text-destructive"} inline-flex items-center gap-1`}
                                                data-testid={`brief-triage-score-${brief.id}`}>
                                                <span className="mono-score font-bold">{brief.triage.score}</span> AI triage
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                                        <span className="inline-flex items-center gap-1"><Building2 size={11} />{brief.company_name || "—"}</span>
                                        <span>· {brief.employer_email}</span>
                                        {brief.job_profile?.location && <span>· {brief.job_profile.location}</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Submitted {brief.created_at ? new Date(brief.created_at).toLocaleString() : ""}
                                    </div>
                                    {brief.triage?.summary && (
                                        <div className="mt-3 rounded-xl bg-violet-500/5 border border-violet-500/20 p-3">
                                            <div className="flex items-center gap-2 text-[10px] font-semibold text-violet-600 uppercase tracking-wider">
                                                <Sparkles size={11} /> AI triage summary
                                                {brief.triage.recommendation && (
                                                    <Badge className="text-[10px] border border-violet-500/30 bg-violet-500/10 text-violet-600 ml-1">
                                                        suggests {brief.triage.recommendation.replace("_", " ")}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="mt-1.5 text-xs text-foreground">{brief.triage.summary}</div>
                                            {(brief.triage.labels?.length > 0 || brief.triage.risk_flags?.length > 0) && (
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {brief.triage.labels?.map((l, i) => (
                                                        <span key={`l${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">{l}</span>
                                                    ))}
                                                    {brief.triage.risk_flags?.map((f, i) => (
                                                        <span key={`r${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/30 inline-flex items-center gap-1">
                                                            <MessageSquare size={10} />{f}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {brief.admin_note && (
                                        <div className="mt-2 rounded-lg bg-muted p-2 text-xs text-foreground">
                                            <span className="font-semibold">Your note:</span> {brief.admin_note}
                                            {brief.admin_reviewer_email && <span className="text-muted-foreground"> · by {brief.admin_reviewer_email}</span>}
                                        </div>
                                    )}
                                    {brief.source_url && (
                                        <a href={brief.source_url} target="_blank" rel="noreferrer"
                                            className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline">
                                            View source URL <ExternalLink size={11} />
                                        </a>
                                    )}
                                </div>
                                {brief.status === "pending_review" && (
                                    <div className="shrink-0 flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => openReview(brief, "approve")}
                                            className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                            data-testid={`brief-approve-${brief.id}`}
                                        >
                                            <Check size={12} /> Approve
                                        </button>
                                        <button
                                            onClick={() => openReview(brief, "info")}
                                            className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                            data-testid={`brief-info-${brief.id}`}
                                        >
                                            <MessageSquare size={12} /> Request info
                                        </button>
                                        <button
                                            onClick={() => openReview(brief, "decline")}
                                            className="text-xs px-3 py-1.5 rounded-full border border-destructive/30 text-destructive hover:bg-destructive/5 inline-flex items-center gap-1"
                                            data-testid={`brief-decline-${brief.id}`}
                                        >
                                            <X size={12} /> Decline
                                        </button>
                                    </div>
                                )}
                                {brief.status === "ready" && (
                                    <div className="shrink-0 text-right">
                                        <div className="text-xs text-muted-foreground">Matches</div>
                                        <div className="mono-score text-lg font-bold text-emerald-600">
                                            {(brief.top_matches || []).length}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
                <DialogContent data-testid="brief-review-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {reviewing?.action === "approve" && <><Check size={16} className="text-emerald-500" /> Approve brief</>}
                            {reviewing?.action === "decline" && <><X size={16} className="text-destructive" /> Decline brief</>}
                            {reviewing?.action === "info" && <><MessageSquare size={16} className="text-amber-500" /> Request info from employer</>}
                        </DialogTitle>
                        <DialogDescription>
                            {reviewing?.action === "approve" && "AI matching will start immediately. The employer will be notified."}
                            {reviewing?.action === "decline" && "The employer will be notified. Their brief will not be matched."}
                            {reviewing?.action === "info" && "The employer will receive a notification with your questions."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Note {reviewing?.action !== "approve" && <span className="text-destructive">*</span>}
                        </label>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={
                                reviewing?.action === "approve" ? "Optional context for the employer…" :
                                    reviewing?.action === "info" ? "What do you need? (e.g. 'Please confirm the salary band.')" :
                                        "Reason for declining (visible to the employer)…"
                            }
                            rows={4}
                            className="mt-1"
                            data-testid="brief-review-note"
                        />
                    </div>
                    <DialogFooter>
                        <button onClick={() => setReviewing(null)} className="btn-outline text-sm px-4 py-2" data-testid="brief-review-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={submit}
                            disabled={submitting || (reviewing?.action !== "approve" && !note.trim())}
                            className={`text-sm px-4 py-2 rounded-full inline-flex items-center gap-1 disabled:opacity-60 ${reviewing?.action === "decline" ? "bg-destructive text-white hover:opacity-90" : "btn-green"}`}
                            data-testid="brief-review-submit"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Confirm
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BriefReviewsTab;
